/**
 * Top Menu Bar - Microsoft Word style navigation
 * Replaces the left sidebar with dropdown menus grouped by category.
 */

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  LayoutDashboard, Users, Shield, ShieldAlert, Calculator, Zap, CalendarDays,
  FileText, Scale, GitCompare, AlertOctagon, CheckCheck, AlertTriangle, FileCheck,
  FileSearch, BookOpen, MessageSquare, Library, Link2, Settings, HelpCircle,
  BarChart3, Sparkles, Building2, Activity, CheckCircle, CreditCard, DollarSign,
  Network, Cpu, Upload, Database, Share2, Microscope, Globe, ClipboardList, Search, Tag,
  FolderOpen, Clock, Image, ChevronDown, ChevronRight, Star, Menu, X, Layers, Briefcase, Download, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '@/components/shared/Logo';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { enablePanelPush, panelPushPermission } from '@/services/api/panel-push';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

interface MenuItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  minPlan?: number;
  description?: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

interface MenuGroup {
  title: string;
  sections: MenuSection[];
  columns?: number; // mega menu: 2 or 3 columns side by side
}

// ==================== Menu Structure ====================

const MENU_GROUPS: MenuGroup[] = [
  {
    title: 'Clients',
    sections: [
      {
        label: 'Management',
        items: [
          { title: 'All Clients', href: '/panel/files/clients', icon: Users, description: 'Manage client portfolio' },
          { title: 'Deadlines', href: '/panel/deadlines', icon: CalendarDays, minPlan: 2, description: 'ATO lodgement tracker' },
          { title: 'Health Scores', href: '/panel/firm/dashboard', icon: Activity, minPlan: 2, description: 'Client health overview' },
        ],
      },
    ],
  },
  {
    title: 'Tax & Compliance',
    columns: 2,
    sections: [
      {
        label: 'Compliance Checks',
        items: [
          { title: 'Compliance Checker', href: '/panel/compliance/checker', icon: Shield, minPlan: 2, description: 'ATO benchmarks' },
          { title: 'Audit Risk', href: '/panel/compliance/audit-risk', icon: ShieldAlert, minPlan: 3, description: 'Audit risk predictor' },
          { title: 'BAS Validator', href: '/panel/compliance/bas', icon: FileCheck, minPlan: 2, description: 'Validate BAS labels' },
          { title: 'Div 7A Checker', href: '/panel/compliance/div7a', icon: Building2, minPlan: 2, description: 'Loans to associates' },
          { title: 'Super Compliance', href: '/panel/compliance/super', icon: Shield, minPlan: 3, description: 'SG tracking' },
          { title: 'FBT Detector', href: '/panel/compliance/fbt', icon: AlertOctagon, minPlan: 3, description: 'FBT triggers' },
          { title: 'Payroll Review', href: '/panel/payroll', icon: Briefcase, minPlan: 2, description: 'Fair Work minimum wage check' },
        ],
      },
      {
        label: 'Tax Planning',
        items: [
          { title: 'Tax Planning', href: '/panel/compliance/tax-planning', icon: CalendarDays, minPlan: 3, description: 'Year-end strategies' },
          { title: 'Tax Opportunities', href: '/panel/compliance/opportunities', icon: Zap, minPlan: 3, description: 'Find deductions' },
          { title: 'Trust Distribution', href: '/panel/compliance/trust-distribution', icon: Scale, minPlan: 3, description: 'Distribution planner' },
          { title: 'CGT Calculator', href: '/panel/compliance/cgt', icon: Calculator, minPlan: 3, description: 'Capital gains tax' },
          { title: 'Missing Docs', href: '/panel/compliance/missing-documents', icon: FileSearch, minPlan: 2, description: 'Document checklist' },
        ],
      },
    ],
  },
  {
    title: 'Financial Analysis',
    sections: [
      {
        label: 'Analysis Tools',
        items: [
          { title: 'Reconciliation', href: '/panel/intelligence/reconciliation', icon: Scale, minPlan: 2, description: 'TB vs P&L vs BS' },
          { title: 'Smart Comparison', href: '/panel/intelligence/comparison', icon: GitCompare, minPlan: 2, description: 'Year-over-year' },
          { title: 'Cash Flow', href: '/panel/compliance/cashflow', icon: BarChart3, minPlan: 3, description: 'Cash flow forecast' },
          { title: 'Risk Warning', href: '/panel/intelligence/failure-warning', icon: AlertOctagon, minPlan: 3, description: 'Business failure alert' },
          { title: 'What-If Scenarios', href: '/panel/compliance/scenarios', icon: Calculator, minPlan: 3, description: 'Scenario modelling' },
        ],
      },
    ],
  },
  {
    title: 'Reports',
    sections: [
      {
        label: 'Generate Reports',
        items: [
          { title: 'Client Reports', href: '/panel/reports', icon: FileText, minPlan: 2, description: '8 report templates' },
        ],
      },
    ],
  },
  {
    title: 'AI Tools',
    sections: [
      {
        label: 'Intelligence',
        items: [
          { title: 'Knowledge Base', href: '/panel/knowledge-base', icon: BookOpen, description: 'Documents & knowledge' },
          { title: 'Global Knowledge', href: '/panel/global-knowledge', icon: Library, description: 'Shared firm knowledge', minPlan: 0 },
          { title: 'ATO Knowledge', href: '/panel/ato-knowledge', icon: BookOpen, description: 'ATO benchmarks & rulings' },
          { title: 'ASIC Knowledge', href: '/panel/asic-knowledge', icon: Shield, description: 'ASIC guides & compliance' },
          { title: 'Fair Work Knowledge', href: '/panel/fair-work-knowledge', icon: Briefcase, description: 'Pay guides & minimum wages' },
          { title: 'Ask All Clients', href: '/panel/multi-client', icon: MessageSquare, minPlan: 3, description: 'Query all clients at once' },
        ],
      },
    ],
  },
  {
    title: 'Workflow',
    sections: [
      {
        label: 'Automation',
        items: [
          { title: 'Workflows', href: '/panel/compliance/workflows', icon: CheckCheck, minPlan: 3, description: 'Workflow templates' },
          { title: 'Reminders', href: '/panel/compliance/reminders', icon: AlertTriangle, minPlan: 2, description: 'Smart reminders' },
        ],
      },
    ],
  },
  {
    title: 'Automation',
    sections: [{
      label: 'Document Automation',
      items: [
        { title: 'Generate Document', href: '/panel/automation', icon: Sparkles, minPlan: 2, description: 'Create personalised client letters' },
        { title: 'Templates', href: '/panel/automation?tab=templates', icon: FileText, minPlan: 2, description: 'Manage letter templates' },
        { title: 'Letterheads', href: '/panel/automation?tab=letterheads', icon: Layers, minPlan: 2, description: 'Firm-branded letterhead templates' },
        { title: 'History', href: '/panel/automation?tab=history', icon: Clock, minPlan: 2, description: 'Generated documents history' },
      ],
    }],
  },
  {
    title: 'More',
    sections: [
      {
        label: 'Tools',
        items: [
          { title: 'Feature Marketplace', href: '/panel/features', icon: Sparkles, description: 'Browse all features' },
          { title: 'Help Center', href: '/panel/help', icon: HelpCircle, description: 'Guides and help' },
        ],
      },
      {
        label: 'Settings',
        items: [
          { title: 'Xero Integration', href: '/panel/integrations/xero', icon: Link2, description: 'Connect Xero' },
          { title: 'Organization', href: '/panel/organization', icon: Network, description: 'Firm structure' },
          { title: 'Users', href: '/panel/users', icon: Users, description: 'Manage users' },
          { title: 'Billing', href: '/panel/billing', icon: CreditCard, description: 'Subscription & billing' },
          { title: 'Settings', href: '/panel/settings', icon: Settings, description: 'App settings' },
        ],
      },
    ],
  },
];

// Ribbon: 15 most important features in a single row
const RIBBON_ITEMS: MenuItem[] = [
  { title: 'Dashboard', href: '/panel/firm/dashboard', icon: LayoutDashboard, minPlan: 2 },
  { title: 'Clients', href: '/panel/files/clients', icon: Users },
  { title: 'AI Chat', href: '/panel/kb-chat', icon: MessageSquare },
  { title: 'Deadlines', href: '/panel/deadlines', icon: CalendarDays, minPlan: 2 },
  { title: 'Compliance', href: '/panel/compliance/checker', icon: Shield, minPlan: 2 },
  { title: 'Audit Risk', href: '/panel/compliance/audit-risk', icon: ShieldAlert, minPlan: 3 },
  { title: 'Reports', href: '/panel/reports', icon: FileText, minPlan: 2 },
  { title: 'Reconciliation', href: '/panel/intelligence/reconciliation', icon: Scale, minPlan: 2 },
  { title: 'BAS Validator', href: '/panel/compliance/bas', icon: FileCheck, minPlan: 2 },
  { title: 'Tax Planning', href: '/panel/compliance/tax-planning', icon: CalendarDays, minPlan: 3 },
  { title: 'Opportunities', href: '/panel/compliance/opportunities', icon: Zap, minPlan: 3 },
  { title: 'Cash Flow', href: '/panel/compliance/cashflow', icon: BarChart3, minPlan: 3 },
  { title: 'Comparison', href: '/panel/intelligence/comparison', icon: GitCompare, minPlan: 2 },
  { title: 'Risk Warning', href: '/panel/intelligence/failure-warning', icon: AlertOctagon, minPlan: 3 },
];

// ==================== Component ====================

export function TopMenuBar({ onMenuClick, favourites }: { onMenuClick?: () => void; favourites?: string[] }) {
  const { hasPlanTier, isSuperAdmin } = useAuthStore();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close menu on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const hasAccess = (item: MenuItem) => item.minPlan === undefined || hasPlanTier(item.minPlan);

  if (isSuperAdmin()) return null; // Super admin uses different layout

  return (
    <div ref={menuRef} className="bg-gradient-to-b from-background to-muted/20">
      {/* Top menu row - fixed to viewport top */}
      <div className="fixed top-0 left-0 right-0 flex items-center px-4 h-11 gap-1 border-b border-border/40 z-40 bg-background">
        <div className="flex items-center gap-1 flex-1 min-w-0 mr-[150px] lg:mr-[260px]">
        {/* Mobile: hamburger toggles the full menu drawer */}
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="lg:hidden flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-muted"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <Link to="/panel" className="flex items-center gap-2 mr-4 shrink-0">
          <Logo alt="RoniDoc" className="h-6 lg:h-7 w-auto" />
        </Link>

        {/* Desktop: horizontal Word-style menus */}
        <div className="hidden lg:flex items-center gap-1">
          {MENU_GROUPS.map(group => (
            <MenuDropdown
              key={group.title}
              group={group}
              isOpen={openMenu === group.title}
              onToggle={() => setOpenMenu(openMenu === group.title ? null : group.title)}
              onClose={() => setOpenMenu(null)}
              hasAccess={hasAccess}
            />
          ))}
        </div>
        </div>
      </div>

      {/* Spacer to offset fixed top menu */}
      <div className="h-11" />

      {/* Ribbon with most-used features (desktop only - mobile uses the drawer) */}
      <div className="hidden lg:block px-4 py-2 border-b border-border/40">
        <RibbonRow items={RIBBON_ITEMS} hasAccess={hasAccess} onItemClick={() => setOpenMenu(null)} />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 top-11 z-30 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="lg:hidden fixed top-11 left-0 bottom-0 z-40 w-[84%] max-w-sm overflow-y-auto border-r border-border bg-background pb-8 shadow-2xl">
            <MobileMenu hasAccess={hasAccess} onNavigate={() => setMobileOpen(false)} />
          </div>
        </>
      )}
    </div>
  );
}

// ==================== Mobile Drawer Menu ====================

function MobileMenu({ hasAccess, onNavigate }: {
  hasAccess: (item: MenuItem) => boolean;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canInstall, isIOS, installed, promptInstall } = usePwaInstall();
  const [open, setOpen] = useState<string | null>(() => {
    // Auto-expand the group containing the current route.
    const g = MENU_GROUPS.find(gr => gr.sections.some(s => s.items.some(i => location.pathname.startsWith(i.href))));
    return g?.title || MENU_GROUPS[0]?.title || null;
  });

  const go = (item: MenuItem) => {
    navigate(hasAccess(item) ? item.href : '/panel/upgrade');
    onNavigate();
  };

  const handleInstall = async () => {
    if (canInstall) { await promptInstall(); onNavigate(); }
    else if (isIOS) { toast('To install: tap the Share button, then "Add to Home Screen".', { duration: 6000 }); }
  };

  const [notifPerm, setNotifPerm] = useState(panelPushPermission());
  const handleEnableNotifs = async () => {
    const res = await enablePanelPush();
    setNotifPerm(panelPushPermission());
    if (res.ok) toast.success('Notifications enabled'); else toast.error(res.reason);
  };

  return (
    <nav className="py-2">
      {!installed && (canInstall || isIOS) && (
        <button
          onClick={handleInstall}
          className="mx-3 mb-2 flex w-[calc(100%-1.5rem)] items-center justify-center gap-2 rounded-lg bg-[#5048e5] px-4 py-2.5 text-sm font-semibold text-white shadow-sm"
        >
          <Download className="h-4 w-4" /> Install app
        </button>
      )}
      {notifPerm !== 'granted' && notifPerm !== 'unsupported' && (
        <button
          onClick={handleEnableNotifs}
          className="mx-3 mb-2 flex w-[calc(100%-1.5rem)] items-center justify-center gap-2 rounded-lg border border-[#5048e5] px-4 py-2.5 text-sm font-semibold text-[#5048e5]"
        >
          <Bell className="h-4 w-4" /> {notifPerm === 'denied' ? 'Notifications blocked' : 'Enable notifications'}
        </button>
      )}
      {MENU_GROUPS.map(group => {
        const isOpen = open === group.title;
        const hasActive = group.sections.some(s => s.items.some(i => location.pathname.startsWith(i.href)));
        return (
          <div key={group.title} className="border-b border-border/40">
            <button
              onClick={() => setOpen(isOpen ? null : group.title)}
              className={cn(
                'flex w-full items-center justify-between px-4 py-3 text-sm font-semibold',
                hasActive ? 'text-[#5048e5]' : 'text-foreground'
              )}
            >
              {group.title}
              <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
            </button>
            {isOpen && (
              <div className="pb-1">
                {group.sections.map((section, si) => (
                  <div key={si}>
                    {section.label && (
                      <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {section.label}
                      </div>
                    )}
                    {section.items.map(item => {
                      const Icon = item.icon;
                      const active = location.pathname.startsWith(item.href);
                      const locked = !hasAccess(item);
                      return (
                        <button
                          key={item.href}
                          onClick={() => go(item)}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm',
                            active ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'text-foreground hover:bg-muted'
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="flex-1 truncate">{item.title}</span>
                          {locked && <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted-foreground">Upgrade</span>}
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ==================== Menu Dropdown ====================

function MenuDropdown({ group, isOpen, onToggle, onClose, hasAccess }: {
  group: MenuGroup;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  hasAccess: (item: MenuItem) => boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasActiveItem = group.sections.some(s => s.items.some(i => location.pathname.startsWith(i.href)));
  const cols = group.columns || 1;

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={cn(
          'px-3 h-8 rounded text-sm font-medium transition-colors flex items-center gap-1',
          isOpen ? 'bg-[#5048e5] text-white' :
          hasActiveItem ? 'bg-[#5048e5]/10 text-[#5048e5]' :
          'hover:bg-muted'
        )}
      >
        {group.title}
        <ChevronDown className={cn('h-3 w-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 mt-0.5 bg-popover border rounded-lg shadow-xl z-50 overflow-hidden',
            cols > 1 ? '' : 'min-w-[320px]',
          )}
          style={{ animationDuration: '150ms', ...(cols > 1 ? { width: cols * 260 } : {}) }}
        >
          <div className={cols > 1 ? `grid grid-cols-${cols}` : ''} style={cols > 1 ? { gridTemplateColumns: `repeat(${cols}, 1fr)` } : {}}>
            {group.sections.map((section, si) => (
              <div key={si} className={cn(cols > 1 ? 'border-r last:border-r-0' : si > 0 && 'border-t')}>
                {section.label && (
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </div>
                )}
                <div className="py-1">
                  {section.items.map(item => {
                    const hasItemAccess = hasAccess(item);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.href}
                        onClick={() => { if (hasItemAccess) { navigate(item.href); onClose(); } else { navigate('/panel/upgrade'); onClose(); } }}
                        className={cn(
                          'w-full flex items-start gap-3 px-3 py-2 hover:bg-muted/80 text-left transition-colors',
                          !hasItemAccess && 'opacity-60'
                        )}
                      >
                        <div className={cn(
                          'h-8 w-8 rounded flex items-center justify-center shrink-0',
                          hasItemAccess ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'bg-muted text-muted-foreground'
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            {item.title}
                            {!hasItemAccess && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Upgrade</span>}
                          </div>
                          {item.description && (
                            <p className="text-[11px] text-muted-foreground truncate">{item.description}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Ribbon ====================

function RibbonRow({ items, hasAccess, onItemClick }: { items: MenuItem[]; hasAccess: (item: MenuItem) => boolean; onItemClick?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden">
      {items.map(item => {
        const Icon = item.icon;
        const hasItemAccess = hasAccess(item);
        const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');

        return (
          <button
            key={item.href}
            onClick={() => { onItemClick?.(); if (hasItemAccess) navigate(item.href); else navigate('/panel/upgrade'); }}
            className={cn(
              'group flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md min-w-[72px] transition-all shrink-0 relative',
              isActive ? 'bg-[#5048e5]/15 text-[#5048e5]' :
              hasItemAccess ? 'hover:bg-muted text-foreground' :
              'hover:bg-muted/50 text-muted-foreground opacity-60'
            )}
            title={hasItemAccess ? item.title : `${item.title} (Upgrade required)`}
          >
            <div className={cn(
              'h-7 w-7 rounded-md flex items-center justify-center transition-all',
              isActive ? 'bg-[#5048e5] text-white' :
              hasItemAccess ? 'bg-gradient-to-br from-[#5048e5]/10 to-[#5048e5]/5 text-[#5048e5] group-hover:from-[#5048e5]/20 group-hover:to-[#5048e5]/10' :
              'bg-muted text-muted-foreground'
            )}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <span className="text-[10px] font-medium leading-tight whitespace-nowrap">{item.title}</span>
          </button>
        );
      })}
    </div>
  );
}
